import { Badge, ChakraSelect, HStack, Text, VStack } from '@elkaso-app/web-design';
import { useState } from 'react';

interface StatusDropdownCellProps {
  locationId: number;
  isMaster: boolean;
  onMasterChange: (locationId: number, isMaster: boolean) => Promise<void>;
  isLoading?: boolean;
}

type StatusOption = {
  value: 'standard' | 'master';
  label: string;
  description: string;
  color: 'gray' | 'yellow';
  icon: string;
};

export const StatusDropdownCell: React.FC<StatusDropdownCellProps> = ({
  locationId,
  isMaster,
  onMasterChange,
  isLoading = false,
}) => {
  const [isChanging, setIsChanging] = useState(false);

  const statusOptions: StatusOption[] = [
    {
      value: 'standard',
      label: 'Standard',
      description: 'Regular location that can be set as child',
      color: 'gray',
      icon: '📍',
    },
    {
      value: 'master',
      label: 'Master',
      description: 'Master location that cannot be set as child',
      color: 'yellow',
      icon: '⭐',
    },
  ];

  const currentValue = statusOptions.find((option) => option.value === (isMaster ? 'master' : 'standard'));

  const handleChange = async (selectedOption: any) => {
    if (!selectedOption) return;

    const newMasterStatus = selectedOption.value === 'master';

    // Don't make API call if status hasn't actually changed
    if (newMasterStatus === isMaster) {
      return;
    }

    setIsChanging(true);
    try {
      await onMasterChange(locationId, newMasterStatus);
    } catch (error) {
      // Error handling is done in the API hook
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div style={{ padding: '8px 4px' }}>
      <ChakraSelect
        value={currentValue}
        onChange={handleChange}
        options={statusOptions}
        isDisabled={isLoading || isChanging}
        isLoading={isChanging}
        isClearable={false}
        isSearchable={false}
        getOptionLabel={(option: any) => option?.label || ''}
        getOptionValue={(option: any) => option?.value || ''}
        formatOptionLabel={(option: any) => (
          <HStack spacing={3} align='center' p={2}>
            <Text fontSize='lg'>{option?.icon || ''}</Text>
            <VStack spacing={0} align='start'>
              <HStack spacing={2} align='center'>
                <Text fontWeight='semibold' fontSize='sm'>
                  {option?.label || ''}
                </Text>
                <Badge colorScheme={option?.color || 'gray'} variant='subtle' size='sm'>
                  {option?.value === 'master' ? 'MASTER' : 'STANDARD'}
                </Badge>
              </HStack>
              <Text fontSize='xs' color='gray.500' lineHeight='1.2'>
                {option?.description || ''}
              </Text>
            </VStack>
          </HStack>
        )}
        styles={{
          control: (provided: any) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '8px',
            borderColor: '#E2E8F0',
            '&:hover': {
              borderColor: '#CBD5E0',
            },
          }),
          valueContainer: (provided: any) => ({
            ...provided,
            padding: '4px 12px',
          }),
          singleValue: (provided: any) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
          }),
        }}
        closeMenuOnSelect={true}
        blurInputOnSelect={false}
        menuPlacement='auto'
        placeholder='Select status...'
      />
    </div>
  );
};
